import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './TaxReports.css';

const TaxReports = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportParams, setReportParams] = useState({
    period: new Date().toISOString().slice(0, 7), // YYYY-MM
    year: new Date().getFullYear().toString()
  });

  // Obtener reportes fiscales
  const { data: reports } = useQuery({
    queryKey: ['taxReports', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/tax-reports`);
      return response.data.data.reports || [];
    },
    enabled: !!companyId
  });

  // Generar reporte de retenciones
  const generateRetentionsMutation = useMutation({
    mutationFn: async (period) => {
      const response = await api.post(`/companies/${companyId}/tax-reports/retenciones?period=${period}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['taxReports', companyId]);
      alert('Reporte de retenciones generado exitosamente');
    }
  });

  // Generar reporte de IVA
  const generateIVAMutation = useMutation({
    mutationFn: async (period) => {
      const response = await api.post(`/companies/${companyId}/tax-reports/iva?period=${period}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['taxReports', companyId]);
      alert('Reporte de IVA generado exitosamente');
    }
  });

  // Generar Formulario 350
  const generateForm350Mutation = useMutation({
    mutationFn: async (year) => {
      const response = await api.post(`/companies/${companyId}/tax-reports/form-350?year=${year}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['taxReports', companyId]);
      alert('Formulario 350 generado exitosamente');
    }
  });

  const handleGenerateReport = () => {
    if (selectedReportType === 'retenciones') {
      generateRetentionsMutation.mutate(reportParams.period);
    } else if (selectedReportType === 'iva') {
      generateIVAMutation.mutate(reportParams.period);
    } else if (selectedReportType === 'form-350') {
      generateForm350Mutation.mutate(reportParams.year);
    }
  };

  return (
    <div className="tax-reports">
      <div className="page-header">
        <h2>Reportes Fiscales</h2>
      </div>

      <div className="reports-container">
        <div className="reports-sidebar">
          <h3>Reportes Fiscales</h3>
          <button
            className={`report-button ${selectedReportType === 'retenciones' ? 'active' : ''}`}
            onClick={() => setSelectedReportType('retenciones')}
          >
            Retenciones
          </button>
          <button
            className={`report-button ${selectedReportType === 'iva' ? 'active' : ''}`}
            onClick={() => setSelectedReportType('iva')}
          >
            IVA
          </button>
          <button
            className={`report-button ${selectedReportType === 'form-350' ? 'active' : ''}`}
            onClick={() => setSelectedReportType('form-350')}
          >
            Formulario 350
          </button>
        </div>

        <div className="reports-main">
          {selectedReportType && (
            <div className="report-generator">
              <h3>
                {selectedReportType === 'retenciones' && 'Reporte de Retenciones'}
                {selectedReportType === 'iva' && 'Reporte de IVA'}
                {selectedReportType === 'form-350' && 'Formulario 350 - Declaración de Renta'}
              </h3>

              <div className="report-params">
                {selectedReportType !== 'form-350' ? (
                  <div className="param-group">
                    <label>Período (YYYY-MM):</label>
                    <input
                      type="month"
                      value={reportParams.period}
                      onChange={(e) => setReportParams({ ...reportParams, period: e.target.value })}
                    />
                  </div>
                ) : (
                  <div className="param-group">
                    <label>Año:</label>
                    <input
                      type="number"
                      min="2020"
                      max="2030"
                      value={reportParams.year}
                      onChange={(e) => setReportParams({ ...reportParams, year: e.target.value })}
                    />
                  </div>
                )}

                <button
                  onClick={handleGenerateReport}
                  className="btn-primary"
                  disabled={
                    (selectedReportType === 'retenciones' && generateRetentionsMutation.isLoading) ||
                    (selectedReportType === 'iva' && generateIVAMutation.isLoading) ||
                    (selectedReportType === 'form-350' && generateForm350Mutation.isLoading)
                  }
                >
                  {generateRetentionsMutation.isLoading || generateIVAMutation.isLoading || generateForm350Mutation.isLoading
                    ? 'Generando...'
                    : 'Generar Reporte'}
                </button>
              </div>
            </div>
          )}

          <div className="reports-list">
            <h3>Reportes Generados</h3>
            {reports && reports.length > 0 ? (
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Período</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Creado por</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map(report => (
                    <tr key={report.id}>
                      <td>
                        <span className={`report-type type-${report.reportType}`}>
                          {report.reportType.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td>{report.period}</td>
                      <td>
                        <span className={`status-badge status-${report.status}`}>
                          {report.status}
                        </span>
                      </td>
                      <td>{new Date(report.createdAt).toLocaleDateString()}</td>
                      <td>{report.createdBy?.name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <p>No hay reportes fiscales generados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxReports;

